import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Printer, RotateCcw } from 'lucide-react';
import dayjs from '../../utils/dayjs';
import { printSaleReceipt } from '../../utils/pdf';

const SaleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [printing, setPrinting] = React.useState(false);
  const [flipping, setFlipping] = React.useState(false);
  const [sale, setSale] = React.useState(null);

  const loadSale = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await salesAPI.getById(id);
      setSale(res.data);
    } catch (err) {
      console.error('Failed to load sale', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    loadSale();
  }, [loadSale]);

  const handleBack = () => navigate('/sales');

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const saleForPrint = JSON.parse(JSON.stringify(sale));
      await printSaleReceipt(saleForPrint);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF receipt. Please try again.');
    } finally {
      setPrinting(false);
    }
  };

  const handleFlipPayment = async () => {
    if (!sale) return;
    
    setFlipping(true);
    try {
      const currentCash = Number(sale.CashReceived || 0);
      const currentBank = Number(sale.BankReceived || 0);
      
      // Flip the values
      const newCash = currentBank;
      const newBank = currentCash;
      
      // Update the sale object
      const updatedSale = {
        ...sale,
        CashReceived: newCash,
        BankReceived: newBank
      };
      
      // Call API to update the sale
      await salesAPI.updatePaymentMode(id, {
        CashReceived: newCash,
        BankReceived: newBank
      });
      
      // Update local state
      setSale(updatedSale);
      
      alert('Payment modes flipped successfully!');
    } catch (error) {
      console.error('Error flipping payment modes:', error);
      alert('Error flipping payment modes. Please try again.');
    } finally {
      setFlipping(false);
    }
  };

  // Check if user is manager (ID 2) or admin
  const canFlipPayment = user && (user.id === 2 || user.is_admin);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-gray-700">Sale not found.</div>
        <button onClick={handleBack} className="mt-4 px-4 py-2 border rounded-lg">Back</button>
      </div>
    );
  }

  const details = (sale.details || []).map((d) => ({
    id: d.ID,
    name: d.item?.Name || 'Item',
    barcode: d.item?.Barcode || '-',
    variationId: d.item?.product_id || '-',
    quantity: d.Quantity,
    price: d.Price,
    discount: d.Discount || 0,
    total: (Number(d.Price || 0) - Number(d.Discount || 0)) * Number(d.Quantity || 0),
    unit: d.item?.Unit || '',
  }));

  const subtotal = details.reduce((acc, d) => acc + d.total, 0);
  const overallDiscount = Number(sale.Discount || 0);
  const netTotal = subtotal - overallDiscount;

  const wcOrders = sale.woocommerce_orders || [];
  const woocommerceId = wcOrders.length
    ? wcOrders
        .map((o) => (wcOrders.length > 1 ? `${o.woocommerce_id} (${o.brand})` : `${o.woocommerce_id}`))
        .join(', ')
    : '-';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sale #{sale.SaleNo || sale.ID}</h1>
          <p className="text-gray-600">{sale.CustomerName || 'Counter Sale'}{sale.CustomerPhone ? ` • ${sale.CustomerPhone}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBack}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {canFlipPayment && (
            <button
              onClick={handleFlipPayment}
              disabled={flipping}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" /> 
              {flipping ? 'Flipping...' : 'Flip Payment'}
            </button>
          )}
          <button
            onClick={handlePrint}
            disabled={printing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div>
            <div className="text-sm text-gray-500">Customer</div>
            <div className="text-gray-900">{'Counter Sale'}</div>
          </div>
      
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Customer Name</div>
            <div className="text-gray-900">{sale.CustomerName || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Phone Number</div>
            <div className="text-gray-900">{sale.CustomerPhone || '-'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sale Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Sale Date</div>
            <div className="text-gray-900">{sale.SaleDate ? dayjs(sale.SaleDate).format('DD MMM YYYY, hh:mm A') : '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">WooCommerce Order ID</div>
            <div className="text-gray-900">{woocommerceId}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Remarks</div>
            <div className="text-gray-900">{sale.SaleRemarks || sale.Remarks || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Cash</div>
            <div className="text-gray-900">{formatPrice(Number(sale.CashReceived || 0))}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Bank</div>
            <div className="text-gray-900">{formatPrice(Number(sale.BankReceived || 0))}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barcode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variation ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {details.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{d.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{d.barcode}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{d.variationId}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{d.quantity} {d.unit}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{formatPrice(Number(d.price || 0))}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{formatPrice(Number(d.discount || 0))}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{formatPrice(Number(d.total || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-gray-500">Subtotal</div>
            <div className="text-lg font-semibold">{formatPrice(subtotal)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Additional Discount</div>
            <div className="text-lg font-semibold">{formatPrice(overallDiscount)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Net Total</div>
            <div className="text-lg font-semibold">{formatPrice(netTotal)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleDetail;






